import { FunctionComponent, useContext, useMemo } from 'react';

import { Switch, LockIcon, Preloader, Button } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { useGetUserByUUID, useBanClient } from 'hooks';
import { LocalizationContext } from 'localization';

import { AdditionalInfo } from './AdditionalInfo';
import s from './ClientCard.module.scss';
import { Comment } from './Comment';
import { Info } from './Info';
import { IClientCardProps } from './interfaces';
import { PausableTariff } from './PausableTariff';

export const ClientCard: FunctionComponent<IClientCardProps> = ({
  clientUUID,
  isInfoOpened,
  close,
  openComment,
  hidePausableTariff,
  setIsDepositOpen,
  setIsDefaultClient,
  notShowButtonDeposit = false,
  setIsResetPasswordModal,
  setCurrentClient,
  showResetPassword = false,
  setIsRoleClient,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const isManage = useMemo<boolean>(
    () => permissions?.has('client_manage') || false,
    [permissions]
  );

  const { data: loadedClient, loading } = useGetUserByUUID(
    clientUUID,
    hidePausableTariff
  );
  const client = loadedClient?.user;

  setCurrentClient && setCurrentClient(client);
  const activeOperator = client?.roles.find(
    (role) => role.alias === 'active_operator'
  );
  setIsRoleClient && setIsRoleClient(activeOperator?.alias);

  const { banClient, loading: banClientLoading } = useBanClient(t);

  return (
    <div className={s.container}>
      <Preloader loading={!client || loading || !permissions}>
        {client && (
          <Info
            client={client}
            close={close}
            setIsDepositOpen={setIsDepositOpen}
            setIsDefaultClient={setIsDefaultClient}
            notShowButtonDeposit={notShowButtonDeposit}
          />
        )}

        <AdditionalInfo
          registrationDate={client?.created_at}
          lastActivity={client?.last_client_activity}
          timeSpent={client?.total_hours}
          defaultState={isInfoOpened}
          firstName={client?.first_name}
        />

        {permissions?.has('comment_read') && (
          <Comment
            comment={client?.last_comment}
            defaultState={
              openComment ? Boolean(client?.last_comment?.text) : isInfoOpened
            }
          />
        )}

        {!hidePausableTariff && (
          <PausableTariff
            pausableInfo={client?.pausable_info}
            nickname={client?.nickname}
          />
        )}

        {isManage && (
          <div className={s['disable-client']}>
            <LockIcon size="m" color="link-info" />
            {t('clients.ban')}
            <Switch
              value={!!client?.banned_at}
              disabled={
                banClientLoading || client?.roles.some(({ id }) => id === 1)
              }
              onChange={(value) =>
                banClient({ variables: { uuid: clientUUID, input: { value } } })
              }
              className={s['ban-toggler']}
            />
          </div>
        )}
        {showResetPassword &&
          client?.unverified &&
          permissions?.has('user_password_reset') && (
            <div className={s.reset_password}>
              <Button
                onClick={() =>
                  setIsResetPasswordModal && setIsResetPasswordModal(true)
                }
                variant="outline"
                fullWidth
              >
                {t('reset.password')}
              </Button>
            </div>
          )}
      </Preloader>
    </div>
  );
};
